import React, { useCallback, useEffect, useState } from "react";
// TablePrimitives retained for other pages; not used here after DataTable refactor
import { IconButton, Menu, MenuItem } from "@mui/material";
import { ChevronDown, MoreHorizontal } from "lucide-react";
import { fetchBusinessUnits, fetchProductLines } from "../services/filtersService";
import { fetchUsers, UsersApiItem, UsersApiResponse, createUser, CreateUserPayload, deleteUserById, Role } from "../services/usersService";
import AvatarImage from "../components/AvatarImage";
import UserDetailsDrawer from "../components/UserDetailsDrawer";
import { showConfirmDeleteModal, showSuccessModal, showErrorModal } from "../components/ui/ConfirmDialog";
import Button from "../components/ui/Button";
import { useNavigate } from "react-router-dom";
import DataTable, { Column } from "../components/ui/DataTable";
import ForbiddenState from "../components/ui/ForbiddenState";
import { isAxiosForbidden } from "../lib/utils";
import { useSecurity } from "../security/SecurityContext";

type UserRow = {
  id: string;
  name: string;
  email: string;
  roles: Role[];
  businessUnit?: string | null;
  productLine?: string | null;
  department?: string | null;
  avatarUri?: string | null;
};

type SortKey = "name" | "email" | "businessUnit" | "productLine" | "roles";
type SortDirection = "asc" | "desc";

const initialUsers: UserRow[] = [];

// Options are loaded from API to mirror DetailsDrawer logic
// Business Units and Product Lines are independent for this drawer

function normalizeSortText(value?: string | null): string {
  return (value || "").trim().toLocaleLowerCase();
}

function getRolesSortValue(roles: Role[]): string {
  if (!Array.isArray(roles) || roles.length === 0) return "";
  return roles
    .map((role) => normalizeSortText(role?.name))
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right))
    .join(" | ");
}

function compareTextValues(left: string, right: string, direction: SortDirection): number {
  const leftEmpty = !left;
  const rightEmpty = !right;

  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;

  const result = left.localeCompare(right);
  return direction === "asc" ? result : -result;
}

function getServerSortField(sortKey: SortKey | null): string {
  switch (sortKey) {
    case "name":
      return "Name";
    case "email":
      return "EmailAddress";
    case "businessUnit":
      return "BusinessUnit";
    case "productLine":
      return "ProductLine";
    default:
      return "";
  }
}

export default function Users() {
  const navigate = useNavigate();
  const { has } = useSecurity();
  const canManageUsers = has("Manage_Users");
  const canManageRoles = has("Manage_Roles");
  const [rows, setRows] = useState<UserRow[]>(initialUsers);
  const [loading, setLoading] = useState<boolean>(false);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [totalResults, setTotalResults] = useState<number>(0);
  const [totalUsers, setTotalUsers] = useState<number | null>(null);
  const [activeUsers, setActiveUsers] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [detailsUserId, setDetailsUserId] = useState<string | null>(null);
  const [detailsStartInEdit, setDetailsStartInEdit] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);

  // Dynamic options from API
  const [buOptions, setBuOptions] = useState<string[]>([]);
  const [plOptions, setPlOptions] = useState<string[]>([]);
  // Roles/groups are fetched inside UserDetailsDrawer; no hardcoded options here
  // Row actions menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const openMenu = Boolean(menuAnchorEl);
  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuRowId(rowId);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };

  const loadUsers = useCallback(async (options?: {
    page?: number;
    itemsPerPage?: number;
    sortKey?: SortKey | null;
    sortDirection?: SortDirection;
  }) => {
    const nextPage = options?.page ?? page;
    const nextItemsPerPage = options?.itemsPerPage ?? itemsPerPage;
    const nextSortKey = options?.sortKey ?? sortKey;
    const nextSortDirection = options?.sortDirection ?? sortDirection;
    const orderByField = getServerSortField(nextSortKey);

    try {
      setLoading(true);
      setForbidden(false);
      const data: UsersApiResponse = await fetchUsers({
        page: nextPage,
        pageSize: nextItemsPerPage,
        skip: Math.max(0, (nextPage - 1) * nextItemsPerPage),
        includeDeleted: false,
        orderByField: orderByField || undefined,
        descending: orderByField ? nextSortDirection === "desc" : false,
      });
      const items: UsersApiItem[] = Array.isArray(data?.results) ? data.results : [];
      const nextTotalResults = typeof data?.totalResults === 'number'
        ? data.totalResults
        : typeof data?.totalCount === 'number'
          ? data.totalCount
          : items.length;
      const nextTotalPages = Math.max(1, Math.ceil(nextTotalResults / nextItemsPerPage));

      if (nextTotalResults > 0 && nextPage > nextTotalPages) {
        setPage(nextTotalPages);
        return;
      }

      setTotalUsers(typeof data?.totalCount === 'number' ? data.totalCount : null);
      setTotalResults(nextTotalResults);
      setActiveUsers(typeof (data as any)?.activeUsers === 'number' ? (data as any).activeUsers : null);
      const baseRows: UserRow[] = items.map((it): UserRow => ({
        id: (it.id || it.userId || "").toString(),
        name: it.name || "",
        email: it.emailAddress || "",
        roles: Array.isArray(it.roles) ? it.roles : [],
        businessUnit: it.businessUnit || null,
        productLine: it.productLine || null,
        department: it.department || null,
        avatarUri: it.avatarUri ?? null,
      }));
      setRows(baseRows);
    } catch (_e) {
      if (isAxiosForbidden(_e)) {
        setForbidden(true);
      }
      setTotalUsers(null);
      setActiveUsers(null);
      setTotalResults(0);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, page, sortDirection, sortKey]);


  const handleSaveCreate = async (data: {
    name: string;
    email: string;
    department: string;
    roles?: Role[];
    businessUnit: string;
    productLine: string;
    avatarUri?: string | null;
  }) => {
    try {
      const payload: CreateUserPayload = {
        userId: null,
        name: data.name || null,
        emailAddress: data.email || null,
        department: data.department || null,
        roles: data.roles,
        businessUnit: data.businessUnit || null,
        productLine: data.productLine || null,
        id: "",
        createdAt: null,
        updatedAt: null,
      };
      if (data.avatarUri) {
        payload.avatarUri = data.avatarUri;
      }
      await createUser(payload);
      await loadUsers();
      await showSuccessModal({ title: 'User created', timer: 1400, showConfirmButton: false });
    } catch (_e) {
      throw new Error('Failed to create user');
    }
  };

  const startCreateUser = () => {
    setCreateOpen(true);
  };

  const openUserDetails = (row: UserRow, startInEdit: boolean = false) => {
    setDetailsUserId(row.id);
    setDetailsStartInEdit(startInEdit);
    setDetailsOpen(true);
    handleMenuClose();
  };


  const deleteUser = async (row: UserRow) => {
    handleMenuClose();
    const confirmed = await showConfirmDeleteModal({ entityLabel: 'User' });
    if (!confirmed) return;
    try {
      await deleteUserById(row.id);
      if (rows.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await loadUsers();
      }
      await showSuccessModal({ title: 'User deleted', timer: 1000, showConfirmButton: false });
    } catch (_e) {
      await showErrorModal({ title: 'Failed to delete user', description: 'Please try again.' });
    }
  };

  // Load Business Units and Product Lines once drawer opens (or on mount)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [bus, lines] = await Promise.all([
          fetchBusinessUnits(),
          fetchProductLines(undefined),
        ]);
        if (!alive) return;
        setBuOptions(Array.isArray(bus) ? bus : []);
        const uniqueLines = Array.isArray(lines) ? Array.from(new Set(lines)) : [];
        // Ensure "All" is present as first option
        const withAll = ["All", ...uniqueLines.filter(l => l !== "All")];
        setPlOptions(withAll);
      } catch (_e) {
        if (!alive) return;
        setBuOptions([]);
        setPlOptions(["All"]);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Reload users whenever server paging or active sort changes
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const getSortValue = (row: UserRow, key: SortKey): string => {
    switch (key) {
      case "name":
        return normalizeSortText(row.name);
      case "email":
        return normalizeSortText(row.email);
      case "businessUnit":
        return normalizeSortText(row.businessUnit);
      case "productLine":
        return normalizeSortText(row.productLine);
      case "roles":
        return getRolesSortValue(row.roles);
      default:
        return "";
    }
  };

  const compareRows = (left: UserRow, right: UserRow): number => {
    if (!sortKey) return 0;

    const primaryResult = compareTextValues(getSortValue(left, sortKey), getSortValue(right, sortKey), sortDirection);
    if (primaryResult !== 0) return primaryResult;

    const nameResult = compareTextValues(getSortValue(left, "name"), getSortValue(right, "name"), "asc");
    if (nameResult !== 0) return nameResult;

    return left.id.localeCompare(right.id);
  };

  const handleSortChange = (key: SortKey) => {
    setPage(1);
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection("asc");
      return;
    }

    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const renderSortableHeader = (label: string, key: SortKey) => (
    <button
      type="button"
      onClick={() => handleSortChange(key)}
      aria-label={`Sort by ${label}`}
      aria-pressed={sortKey === key}
      className="flex items-center gap-1 select-none"
    >
      {label}
      <ChevronDown
        className={`h-3.5 w-3.5 transition-transform ${sortKey === key ? (sortDirection === "asc" ? "rotate-180 opacity-100" : "opacity-100") : "opacity-40"}`}
      />
    </button>
  );

  const sortedRows = sortKey === "roles" ? [...rows].sort(compareRows) : rows;

  const columns: Column<UserRow>[] = [
    {
      id: 'name', header: renderSortableHeader("Name", "name"), width: '18%', align: 'left', cell: (u: UserRow) => (
        <div className="flex items-center gap-2">
          <AvatarImage name={u.name} filename={u.avatarUri || null} size={28} />
          <span>{u.name}</span>
        </div>
      )
    },
    { id: 'email', header: renderSortableHeader("Email", "email"), width: '22%', align: 'left', cell: (u: UserRow) => u.email },
    { id: 'bu', header: renderSortableHeader("Business Unit", "businessUnit"), width: '12%', align: 'left', cell: (u: UserRow) => u.businessUnit || "--" },
    { id: 'pl', header: renderSortableHeader("Product Line", "productLine"), width: '10%', align: 'left', cell: (u: UserRow) => u.productLine || "--" },
    {
      id: 'groups', header: renderSortableHeader("Roles", "roles"), width: '25%', align: 'left', cell: (u: UserRow) => {
        const roles = Array.isArray(u.roles) && u.roles.length > 0 ? u.roles : [];
        return (
          <div className="flex flex-wrap gap-1">
            {roles.length > 0 ? (
              roles.map(role => (
                <span key={role.id} className="text-[11px] px-2 py-0.5 rounded bg-[#E6F0FA] text-[#1D5176] border border-[#C9DDF1]">{role.name}</span>
              ))
            ) : (
              <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-gray-700">--</span>
            )}
          </div>
        );
      }
    },
    {
      id: 'actions', header: 'Actions', width: '4%', align: 'center', cell: (u: UserRow) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e as any, u.id)}>
          <MoreHorizontal className="h-4 w-4 text-gray-500" />
        </IconButton>
      )
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#F7F7F7]">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 flex items-center">
          <h1 className="text-xl font-semibold text-gray-900">Users</h1>
          <div className="ml-6 flex items-center gap-8 text-sm">
            <div className="flex flex-col">
              <span className="text-gray-500">Total Users</span>
              <span className="text-gray-900 font-semibold">{typeof totalUsers === 'number' ? totalUsers : '--'}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500">Active Users</span>
              <span className="text-gray-900 font-semibold">{typeof activeUsers === 'number' ? activeUsers : '--'}</span>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={canManageRoles ? () => navigate('/Roles') : undefined}
              disabled={!canManageRoles}
              title={canManageRoles ? undefined : "You don't have permission to manage roles"}
            >
              Create New Role
            </Button>
            <Button
              onClick={canManageUsers ? startCreateUser : undefined}
              disabled={!canManageUsers}
              title={canManageUsers ? undefined : "You don't have permission to manage users"}
            >
              Add New User
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="p-6">
        <div className="max-w-screen-2xl mx-auto">
          <DataTable<UserRow>
            maxBodyHeight={'calc(100vh - 240px)'}
            className=""
            loading={loading}
            rows={sortedRows}
            columns={columns}
            page={page}
            totalPages={Math.max(1, Math.ceil(totalResults / itemsPerPage))}
            onPageChange={(p) => setPage(p)}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setPage(1); }}
            totalResults={totalResults}
            emptyRender={forbidden ? (
              <ForbiddenState />
            ) : undefined}
          />
          <Menu anchorEl={menuAnchorEl} open={openMenu} onClose={handleMenuClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            {menuRowId && (
              <>
                <MenuItem onClick={() => openUserDetails(rows.find(r => r.id === menuRowId)!, false)}>View User</MenuItem>
                <MenuItem onClick={() => openUserDetails(rows.find(r => r.id === menuRowId)!, true)}>Edit User</MenuItem>
                <MenuItem onClick={() => deleteUser(rows.find(r => r.id === menuRowId)!)}>Delete User</MenuItem>
              </>
            )}
          </Menu>
        </div>
      </div>

      {/* User Details Drawer (view/edit existing) */}
      <UserDetailsDrawer
        open={detailsOpen}
        userId={detailsUserId}
        onClose={() => { setDetailsOpen(false); setDetailsStartInEdit(false); }}
        onSaved={() => { setDetailsOpen(false); loadUsers(); }}
        businessUnitOptions={buOptions}
        productLineOptions={plOptions}
        startInEdit={detailsStartInEdit}
      />

      {/* Create User Drawer */}
      <UserDetailsDrawer
        open={createOpen}
        isCreate={true}
        onClose={() => setCreateOpen(false)}
        initialDraft={{
          name: "",
          email: "",
          department: "",
          roles: [],
          businessUnit: buOptions[0] || "",
          productLine: plOptions[0] || "",
        }}
        businessUnitOptions={buOptions}
        productLineOptions={plOptions}
        onSaveCreate={handleSaveCreate}
      />
    </div>
  );
}


